import { Box, Button, FormHelperText, Typography } from "@mui/material";
import { Track, TRACK_DOCUMENT_TYPES } from "../../../../types";
import { useState } from "react";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import z from "zod";
import { RentalListingSchema } from "@/modules/property-tracks/utils/listing";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { useAddListing } from "@/modules/property-tracks/hooks/listings/add-listing.hook";
import { ListingType } from "@/modules/property-tracks/dtos";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import RentalListingForm from "./form";
import { useTrackDocumentQuery } from "@/modules/property-tracks/hooks/track-document.hook";
import ImageSelector from "@/modules/common/components/input/image-selector";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";

type RentalListingFormFields = z.infer<ReturnType<typeof RentalListingSchema>>;

export default function AddRentalListing({ track }: { track: Track }) {
  const [modalOpen, setModalOpen] = useState(false);

  const { data: images, isLoading: imagesLoading } = useTrackDocumentQuery({
    uid: track.uid,
    documentType: TRACK_DOCUMENT_TYPES.Media,
  });

  const { handleSubmit, reset, control } = useForm<RentalListingFormFields>({
    resolver: zodResolver(RentalListingSchema()),
  });

  const onSubmit: SubmitHandler<RentalListingFormFields> = async (data) => {
    submitAddRentalListing(data);
  };

  const mutation = useAddListing();

  const submitAddRentalListing = (data: RentalListingFormFields) => {
    mutation.mutate(
      {
        trackUid: track.uid,
        listingType: ListingType.RENTAL,
        documentUids: data.documentUids,
        propertyRental: {
          ...data.propertyRental,
          paymentTerms: data.propertyRental?.paymentTerms?.value,
          noBrokerCommission: !data.propertyRental?.noBrokerCommission,
        },
        isRented: data.isRented,
      },
      {
        onSuccess: () => {
          handleCloseModal();
          reset();
          showToast({
            type: ToastType.SUCCESS,
            message: "Listing added successfully",
            toastId: TOAST_IDS.ADD_LISTING_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_LISTING_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };

  const handleModalOpen = () => {
    setModalOpen(true);
    reset();
  };

  const { readonly } = useReadonlyContext();

  return (
    <>
      <Button
        disabled={readonly}
        variant="contained"
        onClick={handleModalOpen}
        sx={(theme) => ({
          display: "flex",
          gap: theme.nawy.spacing.xs,
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          textTransform: "none",
          ...theme.nawy.typography.english.variants.body.md.semibold,
        })}
      >
        Add listing details
      </Button>
      <EditFieldModal
        open={modalOpen}
        setOpen={setModalOpen}
        header="Add listing"
        width={480}
        handleConfirm={handleSubmit(onSubmit)}
        handleCloseModal={handleCloseModal}
        isLoading={mutation.isPending}
      >
        <RentalListingForm
          control={control}
          namePrefix="propertyRental"
          propertyUid={track.property.uid}
        />

        <Controller
          control={control}
          name={"documentUids"}
          render={({ field, fieldState }) => (
            <Box>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
              >
                Listing Media
              </Typography>
              <Typography
                sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
              >
                Select photos
              </Typography>

              <ImageSelector
                images={images ?? []}
                loading={imagesLoading}
                selectedImagesIds={field.value ?? []}
                setSelectedImagesIds={(docs: string[]) => field.onChange(docs)}
              />
              {fieldState.error && (
                <FormHelperText error>{fieldState.error.message}</FormHelperText>
              )}
            </Box>
          )}
        ></Controller>
      </EditFieldModal>
    </>
  );
}
